import type { InlineInputProps } from '@ztechapps/webui.input'
import type { ReactNode } from 'react'
import map from 'lodash/map'
import { Icon, ShowIf } from '@ztechapps/webui.util'
import { CustomerAddress } from '@ztechapps/customer.webui'
import { AddressEditField } from '@ztechapps/webui.location'
import { AutoGenerateAddressButton } from './AutoGenerateAddressButton'
import { generateId, humanize } from '@ztechapps/common.utils'
import { orderUpdateOrderAddressQuery } from '@ztechapps/order.common'
import type { User } from '@ztechapps/common.contract'
import type { SetOptional, SetRequired } from 'type-fest'

type InputProps = {
  addressType: 'billing' | 'shipping'
  collaborators?: User[]
  customerId: string
  orderId: string
  companyId: string
  // When set (storefront call sites), the AI auto-generate-address wand is suppressed.
  // Admin call sites omit it, so the wand stays.
  hideAiWand?: boolean
}

export type OrderAddressInlineInputProps = SetOptional<
  SetRequired<InlineInputProps<InputProps>, 'inputProps' | 'parentInputComponent'>,
  'name'
> & { disabledReason?: string }
export const OrderAddressInlineInput = ({
  mutationConfig,
  updateParams,
  emptyContent,
  name,
  label,
  initialValues,
  inputProps,
  canEdit,
  disabledReason,
  parentInputComponent,
}: OrderAddressInlineInputProps) => {
  const addressType = inputProps.addressType ?? 'billing'
  const collaborators = inputProps?.collaborators ?? []

  const collaboratorsId = map(collaborators, (e) => e?._id)

  name = name ?? `${addressType}Address`

  // `canEdit` is a straight pass-through: `OrderAddress.tsx` computes and supplies it directly
  // from `useSalesItemContext()`'s `canEditBeforeTransit` — the same role, document-status and
  // lock check `Context.tsx:44-48` derives. No fallback here: unlike `initialValues` (which the
  // `emptyContent`/`INLINE` paths can partly get by without), every real caller supplies
  // `canEdit`, so a wrapper-level re-derivation would be dead code this library also could not
  // reach correctly (this library cannot import that app-level context —
  // `@nx/enforce-module-boundaries` forbids a `scope:domain` webui library depending on the
  // admin app).

  emptyContent = emptyContent ?? (
    <>
      <Icon name="map-marker" /> <small>{`[Add ${humanize(addressType)} address]`}</small>
    </>
  )

  updateParams =
    updateParams ??
    (({ _id, updatedValue }) => {
      return {
        input: {
          _id,
          address: {
            ...updatedValue,
          },
          addressType,
          customerId: inputProps.customerId,
          orderId: inputProps.orderId,
        },
      }
    })

  mutationConfig = mutationConfig ?? { query: orderUpdateOrderAddressQuery }
  label = label ?? `${humanize(addressType)} address`

  return (
    // `name` already encodes the address type ('billingAddress' | 'shippingAddress'), so it
    // uniquely identifies which of the two order surfaces is open.
    <AddressEditField
      editorKey={name}
      name={name}
      label={label as string}
      initialValues={initialValues}
      mutationConfig={mutationConfig}
      updateParams={updateParams}
      canEdit={canEdit}
      disabledReason={disabledReason}
      parentInputComponent={parentInputComponent}
      addressSelectComponent={CustomerAddress}
      buildFilter={(showAllAddress) => {
        if (showAllAddress)
          return {
            'company._id': inputProps?.companyId,
            addressType: `${addressType}`,
          }

        return {
          _id: { $in: [inputProps?.customerId, ...collaboratorsId] },
          addressType: `${addressType}`,
        }
      }}
      extraActions={({ onAddressSelected, submitting: isBusy }) => (
        <ShowIf condition={!inputProps?.hideAiWand}>
          <AutoGenerateAddressButton
            orderId={inputProps?.orderId}
            onAddressSelected={(address) => onAddressSelected({ ...address, _id: address._id ?? generateId() })}
            disabled={isBusy}
          />
        </ShowIf>
      )}
      emptyContent={emptyContent as ReactNode}
    />
  )
}
